import type { DateRange } from '@hotjar/webapp/constants/DateFilterRanges';
import { getAbsoluteDateRange } from '@hotjar/webapp/constants/DateFilterRanges';

import {
  mapHighlightDtoToHeatmapHighlightModel,
  mapSelectedAreaDtoToModel,
} from './heatmap/mapper';
import { mapHighlightDtoToRecordingHighlightModel } from './recording/mapper';
import {
  mapGenericHighlightDtoToModel,
  mapGenericHighlightsListItemDtoToModel,
  mapHighlightContentStatus,
  mapStringToDeviceType,
} from './common/mapper';

import type {
  Highlight as HighlightModel,
  HighlightFilters as HighlightFiltersModel,
  HighlightsListItem as HighlightsListItemModel,
  HighlightEventMetadata as HighlightEventMetadataModel,
} from './model';

import type {
  Highlight as HighlightDto,
  HighlightFilters as HighlightFiltersDto,
  HighlightsListItem as HighlightsListItemDto,
  HighlightEventMetadata as HighlightEventMetadataDto,
} from './dto';

export function mapHighlightDtoToModel(dto: HighlightDto): HighlightModel {
  if (dto.type === 'heatmap') {
    return mapHighlightDtoToHeatmapHighlightModel(dto);
  }

  if (dto.type === 'recording') {
    return mapHighlightDtoToRecordingHighlightModel(dto);
  }

  return {
    ...mapGenericHighlightDtoToModel(dto),
    type: 'unknown',
    metadata: {},
  };
}

function mapDateRangeModelToDto(model: DateRange | undefined) {
  if (!model) {
    return {
      date_from: undefined,
      date_to: undefined,
    };
  }

  // eslint-disable-next-line camelcase
  const [date_from, date_to] = getAbsoluteDateRange(model);

  // eslint-disable-next-line camelcase
  return { date_from, date_to };
}

export function mapHighlightFiltersModelToDto(model: HighlightFiltersModel): HighlightFiltersDto {
  return {
    ...mapDateRangeModelToDto(model.dateRange),
    label_ids: Array.from(model.labels ?? [], Number),
    types: model.sources ?? [],
    user_ids: (model.authors ?? []).map(({ id }) => id),
    solved_status: model.status,
    page: model.currentPage,
    items_per_page: model.itemsPerPage,
  };
}

export function mapHighlightsListItemDtoToModel(
  dto: HighlightsListItemDto,
): HighlightsListItemModel {
  const genericModel = mapGenericHighlightsListItemDtoToModel(dto);

  if (dto.type === 'heatmap') {
    return {
      ...genericModel,
      metadata: {
        deviceType: mapStringToDeviceType(dto.metadata.device_type),
        selectedArea: mapSelectedAreaDtoToModel(dto.metadata.selected_area),
        clicksCount: dto.metadata.clicks_count,
        clicksPercentage: dto.metadata.clicks_percentage,
      },
      type: 'heatmap',
    };
  }

  if (dto.type === 'recording') {
    return {
      ...genericModel,
      metadata: {
        videoUrl: dto.metadata.video_url,
        clip: {
          duration: dto.metadata.highlight_duration,
        },
      },
      type: 'recording',
      status: dto.status === 'done' ? 'done' : mapHighlightContentStatus(dto.status),
    };
  }

  return {
    ...genericModel,
    type: 'unknown',
    metadata: {},
  };
}

export function mapHighlightEventMetadataModelToDto(
  model?: HighlightEventMetadataModel,
): HighlightEventMetadataDto | undefined {
  if (model?.highlightArea) {
    return {
      highlight_area: model.highlightArea,
    };
  }

  if (model?.uiPosition) {
    return {
      ui_position: model.uiPosition,
    };
  }

  return undefined;
}
